/**
 * Utility functions for handling locality codes in URLs
 * These functions ensure locality codes are stripped from client-facing URLs
 * while maintaining them for backend processing
 */

// All possible locale code variations that might appear in URLs
export const LOCALE_CODES = new Set([
  // Hyphenated versions (standard format)
  'en-us',
  'en-ca',
  'fr-ca',
  'es-mx',
  'es-419',
  'pt-br',
  // Underscore versions (alternative format)
  'en_us',
  'en_ca',
  'fr_ca',
  'es_mx',
  'es_419',
  'pt_br',
]);

/**
 * Checks if a given string is a locality code
 * @param segment - URL segment to check
 * @returns boolean indicating if the segment is a locality code
 */
export const isLocalityCode = (segment: string): boolean => {
  return LOCALE_CODES.has(segment.toLowerCase());
};

/**
 * Strips locality codes from a URL path
 * @param pathname - The pathname to clean
 * @returns Clean pathname without locality codes
 */
export const stripLocalityCodeFromPath = (pathname: string): string => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return '/';
  }

  // Check if first segment is a locality code
  if (isLocalityCode(segments[0])) {
    // Remove the locality code segment
    const cleanSegments = segments.slice(1);
    return cleanSegments.length > 0 ? `/${cleanSegments.join('/')}` : '/';
  }

  // Check for redirect URLs without locality codes (e.g., /redirect/nonsense)
  if (segments[0]?.toLowerCase() === 'redirect' && segments.length > 1) {
    // Remove the redirect prefix (matching production behavior)
    const cleanSegments = segments.slice(1);
    return cleanSegments.length > 0 ? `/${cleanSegments.join('/')}` : '/';
  }

  return pathname;
};

/**
 * Strips locality codes from a complete URL
 * @param url - The URL to clean
 * @returns Clean URL without locality codes, or false if no changes needed
 */
export const stripLocalityCodeFromUrl = (url: string | URL): string | false => {
  const urlObj = typeof url === 'string' ? new URL(url) : url;
  const { pathname, search, hash } = urlObj;

  const cleanPathname = stripLocalityCodeFromPath(pathname);

  // Remove slug parameter if present
  const searchParams = new URLSearchParams(search);
  searchParams.delete('slug');
  const cleanSearch = searchParams.toString() ? `?${searchParams}` : '';

  const cleanUrl = `${cleanPathname}${cleanSearch}${hash}`;
  const originalUrl = `${pathname}${search}${hash}`;

  return cleanUrl !== originalUrl ? cleanUrl : false;
};

/**
 * Gets the locality code from a URL path if present
 * @param pathname - The pathname to check
 * @returns The locality code if found, null otherwise
 */
export const getLocalityCodeFromPath = (pathname: string): string | null => {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length === 0) {
    return null;
  }

  // Check first segment
  if (isLocalityCode(segments[0])) {
    return segments[0].toLowerCase();
  }

  // Check for redirect URLs
  if (segments[0]?.toLowerCase() === 'redirect' && segments.length > 1 && isLocalityCode(segments[1])) {
    return segments[1].toLowerCase();
  }

  return null;
};

/**
 * Validates that a URL doesn't contain locality codes in client-facing paths
 * This is useful for testing and debugging
 * @param url - The URL to validate
 * @returns Object with validation result and details
 */
export const validateCleanUrl = (url: string | URL): { isClean: boolean; localityCode?: string; cleanUrl?: string } => {
  const urlObj = typeof url === 'string' ? new URL(url) : url;
  const localityCode = getLocalityCodeFromPath(urlObj.pathname);

  if (localityCode) {
    const cleanUrl = stripLocalityCodeFromUrl(urlObj);
    return {
      isClean: false,
      localityCode,
      cleanUrl: cleanUrl || urlObj.toString(),
    };
  }

  return { isClean: true };
};
